import React, { useRef, useState, useEffect } from 'react'
import { NonprofitDetailsComponentProps } from './non-profit-details-component.types'
import { useNonprofitDetailsComponentStyles } from './non-profit-details-component.styles'
import Grid from '@material-ui/core/Grid'
import { InfoComponent } from './info-component'
import Button from '@material-ui/core/Button'
import { useTranslation } from 'react-i18next'
import Paper from '@material-ui/core/Paper'
import { SimilarNonprofits } from '../similar-nonprofits-component/similar-nonprofits-component'
import { NonprofitItems } from '../nonprofit-items-component/nonprofit-items-component'
import { baseURL } from '../../../api'
import NonprofitDetailPageDefaultImage from '../../../assets/nonprofit-card.svg'
import Skeleton from '@material-ui/lab/Skeleton'
import Box from '@material-ui/core/Box'
import { PageWrapper } from '../../../components/page-wrapper'

export const NonprofitDetailsComponent: React.FC<NonprofitDetailsComponentProps> =
  (props: NonprofitDetailsComponentProps) => {
    const {
      categoryId,
      imageId,
      externalId,
      webSite,
      name,
      description,
      loading,
      categoryName,
      nonProfitId,
    } = props
    const classes = useNonprofitDetailsComponentStyles()
    const { t } = useTranslation()
    const [noImage, setNoImage] = useState<boolean>(false)

    const [similarNonprofits, setSimilarNonprofits] = useState(true)
    const [nonprofitItems, setNonprofitItems] = useState(true)

    const nonprofitItemsSection = useRef<HTMLDivElement>(null)
    const similarNonprofitsSection = useRef<HTMLDivElement>(null)

    // Check if image loads successfully
    useEffect(() => {
      setNoImage(false) // Reset when externalId changes
      if (externalId) {
        const img = new Image()
        img.src = `${baseURL}/nonprofits/external/image/${externalId}`
        img.onerror = () => {
          setNoImage(true)
        }
      }
    }, [externalId])

    const listedItemsButtonHandler = () => {
      if (nonprofitItemsSection.current !== null)
        nonprofitItemsSection.current.scrollIntoView({ behavior: 'smooth' })
    }

    const similarNonprofitsButtonHandler = () => {
      if (similarNonprofitsSection.current !== null)
        similarNonprofitsSection.current.scrollIntoView({ behavior: 'smooth' })
    }

    const getImageUrl = () => {
      if (noImage || !externalId) {
        return NonprofitDetailPageDefaultImage
      }
      return `${baseURL}/nonprofits/external/image/${externalId}`
    }

    const imgBlock = () => {
      const imageUrl = getImageUrl()
      return (
        <div
          className={classes.imageBackground}
          style={{
            backgroundImage: `url(${imageUrl})`,
          }}
        />
      )
    }

    const imgPlaceholderBlock = () => (
      <Box pt={0.5}>
        <Skeleton animation="wave" variant="rect" className={classes.imgSize} />
      </Box>
    )

    return (
      <div>
        <Paper>
          <div className={classes.mainHolder}>
            <PageWrapper
              title={''}
              displayGoBackButton={true}
              goBackNavPath={"/nonprofit-catalog"}
            >
              <Grid container spacing={3} className={classes.details}>
                <Grid
                  item
                  xs={12}
                  md={5}
                  className={classes.imgPositioningInContainer}
                >
                  {loading ? imgPlaceholderBlock() : imgBlock()}
                </Grid>
                <Grid item xs={12} md={7}>
                  <InfoComponent
                    categoryId={categoryId}
                    description={description}
                    loading={loading}
                    externalId={externalId}
                    name={name}
                    imageId={imageId}
                    webSite={webSite}
                    categoryName={categoryName}
                    nonProfitId={nonProfitId}
                    similarNonprofitsButtonHandler={
                      similarNonprofitsButtonHandler
                    }
                    similarNonprofits={similarNonprofits}
                  />
                </Grid>
              </Grid>
            </PageWrapper>
          </div>
        </Paper>
        {nonprofitItems && (
          <Button
            variant="outlined"
            onClick={listedItemsButtonHandler}
            className={[classes.bold, classes.nonprofitListedItemsButton].join(
              ' '
            )}
          >
            {t('nonprofitCatalog.listedItems')}
          </Button>
        )}

        <section id="nonprofitItems" ref={nonprofitItemsSection}>
          <NonprofitItems
            setNonprofitItems={setNonprofitItems}
            nonprofitId={nonProfitId}
            categoryId={Number.parseInt(categoryId)}
          />
        </section>
        <section
          id="similarNonprofits"
          ref={similarNonprofitsSection}
          className={classes.similarNonprofitsSection}
        >
          <SimilarNonprofits
            setSimilarNonprofits={setSimilarNonprofits}
            nonprofitId={nonProfitId}
            categoryId={Number.parseInt(categoryId)}
          />
        </section>
      </div>
    )
  }
